import {
  useGetProperty,
  useGetPropertyStats,
  useListResidents,
  type ResidentListOut,
} from '@myrentalmate/api-client';
import { Alert, Button, CircularProgress, Link as MuiLink, Stack, Typography } from '@mui/material';
import { Link, useParams } from 'react-router';

import { EmailLink, PhoneLink } from '../../components/ContactLinks';
import { DataTable, type DataColumn } from '../../components/DataTable';
import { StatRow, StatTile } from '../../components/Stats';
import { okData, okItems } from '../../lib/apiData';
import { formatMoney, formatOccupancy } from '../../lib/format';
import { useCan } from '../../lib/permissions';
import { RecordExtras } from '../records/RecordExtras';
import { LateFeeRuleSection } from './LateFeeRuleSection';
import { OwnershipEditor } from './OwnershipEditor';
import { PROPERTY_TYPE_LABELS } from './PropertiesPage';
import { UnitsSection } from './UnitsSection';

const RESIDENT_COLUMNS: DataColumn<ResidentListOut>[] = [
  { label: 'Name', render: (r) => `${r.last_name}, ${r.first_name}` },
  { label: 'Unit', render: (r) => r.unit_number },
  { label: 'Phone', render: (r) => <PhoneLink phone={r.phone} /> },
  { label: 'Email', render: (r) => <EmailLink email={r.email} /> },
];

function PropertyResidents({ propertyId }: { propertyId: number }) {
  const list = useListResidents({ property_id: propertyId, page_size: 200 });
  const residents = okItems(list.data);
  return (
    <Stack spacing={1}>
      <Typography variant="h6">Residents</Typography>
      {residents.length === 0 ? (
        <Typography color="text.secondary">
          No residents here yet. Add them from{' '}
          <MuiLink component={Link} to="/staff/residents">
            Residents
          </MuiLink>
          .
        </Typography>
      ) : (
        <DataTable columns={RESIDENT_COLUMNS} rows={residents} rowKey={(r) => r.id} />
      )}
    </Stack>
  );
}

export function PropertyDetailPage() {
  const propertyId = Number(useParams().propertyId);
  const { data, isPending } = useGetProperty(propertyId);
  const canReadOwners = useCan('owners.read');
  const canManage = useCan('properties.manage');
  const property = okData(data);
  const stats = okData(useGetPropertyStats(propertyId).data);

  if (isPending) return <CircularProgress aria-label="Loading" />;
  if (!property) {
    return (
      <Stack spacing={2} sx={{ alignItems: 'flex-start' }}>
        <Alert severity="error">We couldn't find that property.</Alert>
        <Button component={Link} to="/staff/properties">
          Back to properties
        </Button>
      </Stack>
    );
  }

  return (
    <Stack spacing={4}>
      <div>
        <Button component={Link} to="/staff/properties" size="small" sx={{ ml: -1 }}>
          ‹ Properties
        </Button>
        <Typography variant="h4">{property.name}</Typography>
        <Typography color="text.secondary">
          {PROPERTY_TYPE_LABELS[property.property_type]} · {property.address_line1}
          {property.address_line2 ? `, ${property.address_line2}` : ''}, {property.city},{' '}
          {property.state} {property.postal_code}
        </Typography>
        {property.notes && <Typography sx={{ mt: 1 }}>{property.notes}</Typography>}
      </div>
      <StatRow>
        <StatTile label="Units" value={stats?.units} />
        <StatTile
          label="Occupancy"
          value={formatOccupancy(stats?.occupied_units, stats?.vacant_units)}
          hint={stats && `${stats.occupied_units} occupied, ${stats.vacant_units} vacant`}
        />
        <StatTile
          label="Unavailable"
          value={stats?.unavailable_units}
          hint="Not counted in occupancy"
        />
        <StatTile
          label="Market Rent / Month"
          value={stats && formatMoney(stats.market_rent)}
          hint="All units, until leases are added"
        />
      </StatRow>
      <UnitsSection propertyId={propertyId} />
      <PropertyResidents propertyId={propertyId} />
      {canReadOwners && <OwnershipEditor propertyId={propertyId} />}
      <LateFeeRuleSection propertyId={propertyId} canManage={canManage} />
      <RecordExtras entityType="property" entityId={propertyId} canManage={canManage} />
    </Stack>
  );
}
